import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import DisclaimerBody from "@/components/DisclaimerBody";

/**
 * End-User License Agreement consent gate shown on first entry to a
 * generation engine (Intake / Discernment / Progress / Chat) per
 * browser session. Acceptance is stored in `sessionStorage` so the
 * modal re-appears the next time the user opens the site (per the
 * user's Feb 2026 directive — "Per-session — show again the next
 * time they open the site").
 *
 * Feb 2026 UX update: the full EULA body is HIDDEN by default. The
 * user sees a short summary line + the Continue button + a "Review
 * Terms" toggle. Clicking "Review Terms" expands the full terms
 * inline. This keeps the consent flow non-overwhelming for first-
 * time users while still making the full agreement available to
 * anyone who wants to read before agreeing.
 *
 * Behaviour:
 *  - Continue button → records acceptance, dismisses, lets the user
 *    proceed.
 *  - Review Terms → expands / collapses the full EULA inline.
 *  - Escape key or backdrop click → dismisses WITHOUT recording
 *    consent, and routes the user back to "/" (Landing) so they
 *    can't slip into the engine without acknowledging.
 *
 * Storage key strategy: ONE acceptance covers all four engines for
 * the session (the EULA doesn't vary). Once accepted on any engine,
 * the user won't see it again until the session ends.
 */
const STORAGE_KEY = "rm_disclaimer_accepted_session_v1";

function hasAcceptedThisSession() {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function markAcceptedThisSession() {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, "1");
  } catch {
    /* ignore — Safari private mode etc. */
  }
}

export default function DisclaimerGate({ children }) {
  const [open, setOpen] = useState(() => !hasAcceptedThisSession());
  // Feb 2026: terms collapsed by default — user must click "Review
  // Terms" to expand. The Continue button stays visible regardless
  // so the consent flow remains a single click for users who don't
  // want to read.
  const [termsExpanded, setTermsExpanded] = useState(false);
  const navigate = useNavigate();
  const previouslyFocused = useRef(null);
  const continueBtnRef = useRef(null);

  // Lock body scroll while the modal is open and trap focus on the
  // Continue button so keyboard users can press Enter / Space to
  // consent immediately.
  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => {
      continueBtnRef.current?.focus();
    }, 0);
    return () => {
      clearTimeout(t);
      document.body.style.overflow = prevOverflow;
      if (previouslyFocused.current && previouslyFocused.current.focus) {
        try { previouslyFocused.current.focus(); } catch { /* noop */ }
      }
    };
  }, [open]);

  // Escape dismisses without consent and routes home.
  useEffect(() => {
    if (!open) return;
    function onKey(e) {
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
        navigate("/");
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, navigate]);

  function onAccept() {
    markAcceptedThisSession();
    setOpen(false);
  }

  function onBackdropClick() {
    setOpen(false);
    navigate("/");
  }

  return (
    <>
      {children}
      {open && (
        <div
          className="rm-disclaimer-backdrop"
          role="presentation"
          onClick={onBackdropClick}
          data-testid="disclaimer-gate-backdrop"
        >
          <div
            className="rm-disclaimer-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="rm-disclaimer-title"
            aria-describedby="rm-disclaimer-body"
            onClick={(e) => e.stopPropagation()}
            data-testid="disclaimer-gate-modal"
          >
            <h2
              id="rm-disclaimer-title"
              className="rm-disclaimer-title"
              data-testid="disclaimer-gate-title"
            >
              End-User License Agreement &amp; Terms of Service
            </h2>
            <aside
              className="rm-disclaimer-spirit-notice"
              data-testid="disclaimer-gate-spirit-notice"
              aria-label="Spiritual use notice — read before continuing"
            >
              <p className="rm-disclaimer-spirit-notice-heading">
                Before you go any further&hellip;
              </p>
              <p>
                This App does not replace the Holy Spirit. Outputs are
                intended to present truth to prayerfully ponder. The
                app is a tool to aid discernment but only Jesus Christ
                is THE TRUTH and no machine can ever replace a
                relationship with Him. DO NOT use this tool as your
                only source of truth or attempt to turn it into an
                Idol. Please use it for its intended purpose. A tool
                to present biblical truths and patterns that can help
                you on your journey with God.
              </p>
            </aside>
            <div
              id="rm-disclaimer-body"
              data-testid="disclaimer-gate-body"
            >
              <p className="rm-disclaimer-summary">
                Before using The Restoration Matrix™, please review
                and agree to the End-User License Agreement, Terms of
                Service, Privacy Notice, and Use Terms covering The
                New Jerusalem Codex™ ecosystem. The platform provides
                religious, theological, pastoral, and educational
                tools only — it is not a medical, psychiatric, or
                clinical service.
              </p>
              <button
                type="button"
                className="rm-disclaimer-toggle"
                onClick={() => setTermsExpanded((v) => !v)}
                aria-expanded={termsExpanded}
                aria-controls="rm-disclaimer-full-terms"
                data-testid="disclaimer-gate-review-toggle"
              >
                {termsExpanded ? "Hide Terms" : "Review Terms"}
              </button>
              {termsExpanded && (
                <div
                  id="rm-disclaimer-full-terms"
                  className="rm-disclaimer-full-terms"
                  data-testid="disclaimer-gate-full-terms"
                >
                  <DisclaimerBody showConsentFooter={true} />
                </div>
              )}
              {!termsExpanded && (
                <p className="rm-disclaimer-consent-line">
                  By clicking Continue, you acknowledge and agree to
                  the full terms.
                </p>
              )}
            </div>
            <div className="rm-disclaimer-actions">
              <button
                ref={continueBtnRef}
                type="button"
                className="rm-btn-solid rm-disclaimer-continue"
                onClick={onAccept}
                data-testid="disclaimer-gate-continue"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Exposed for tests / dev tooling that need to reset session state.
export function _resetDisclaimerForTests() {
  try { window.sessionStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
}
